import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "focus-visible:ring-ring flex items-center justify-center whitespace-nowrap rounded-xl text-base font-semibold transition-colors focus-visible:outline-none focus-visible:ring-1 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground active:bg-primary/90 disabled:text-primary-foreground/50",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:text-destructive-foreground/50",
        outline:
          "border-input hover:bg-accent border bg-background hover:text-accent-foreground disabled:text-accent-foreground/50",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 disabled:text-secondary-foreground/50",
        ghost: "hover:bg-accent hover:text-accent-foreground disabled:text-accent-foreground/50",
        link: "text-primary underline-offset-4 hover:underline disabled:text-primary/50",
      },
      size: {
        default: "h-12 w-full px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
